import Settings from '../domain/Settings';
import performance from '../utils/performance';

declare global {
	interface Window {
		__tcfapi?: (action: string, version: number, callback: (tcData: AnyObject, tcSuccess: boolean) => void) => void;
	}
}

type Consents = {
	purposes: Record<string, boolean>
	vendors: Record<string, boolean>
}

let enabled = false,
	tcfConsentListener = false,
	consents: Consents = null,
	onConsentCallbacks = [];

export function init(settings: Settings): void {
	enabled = !!settings.features.privacy;
	onConsentCallbacks = [];
	consents = null;

	if (enabled) {
		performance.mark('privacy - start');
		_registerListener();
	}
}

function _registerListener() {
	_waitForTCFAPI(() => {
		performance.mark('privacy - api available');

		if (tcfConsentListener) {
			window.__tcfapi('removeEventListener', 2, _onTCFConsent);
		}

		window.__tcfapi('addEventListener', 2, _onTCFConsent);
		tcfConsentListener = true;
	}, 0);
}

function _waitForTCFAPI(cb, iteration) {
	if (typeof window.__tcfapi === 'function') {
		cb();

		return;
	}

	setTimeout(() => _waitForTCFAPI(cb, ++iteration), iteration * 100);
}

function _onTCFConsent(tcData, tcDataSuccess) {
	if (tcDataSuccess && ['tcloaded', 'useractioncomplete'].indexOf(tcData.eventStatus) !== -1) {
		performance.mark('privacy - tcData');

		_updateConsent(tcData);
	}
}

function _updateConsent(tcData) {
	consents = {
		'purposes': tcData.purpose.consents,
		'vendors': tcData.vendor.consents
	};

	_loadConsentedSources();

	onConsentCallbacks.forEach(cb => cb(consents));
}

export function onConsent(callback: (consents?: Consents) => void): void {
	if (!enabled) {
		callback();

		return;
	}

	onConsentCallbacks.push(callback);

	if (consents) {
		callback(consents);
	}
}

function _loadConsentedSources() {
	const srcAttribute = 'data-advert-src',
		vendorAttribute = 'data-advert-vendors';

	document.querySelectorAll('[' + srcAttribute + ']').forEach((el) => {
		const vendors = el.getAttribute(vendorAttribute);

		// Any vendor doesn't have consent?
		if (vendors?.split(',').some((v) => !consents.vendors[v])) {
			return;
		}

		el.setAttribute('src', el.getAttribute(srcAttribute));
		el.removeAttribute(srcAttribute);
		el.removeAttribute(vendorAttribute);
	});
}

export function hasXandrConsent(): boolean {
	return hasPurposesConsent([1, 2, 7, 10]) && hasVendorsConsent([32]);
}

export function hasUserProfilingConsent(): boolean {
	return hasPurposesConsent([3, 4]);
}

export function hasVendorsConsent(vendors): boolean {
	if (!enabled) {
		return true;
	}

	return vendors.every((v) => consents?.vendors[v]);
}

export function hasPurposesConsent(purposes) {
	if (!enabled) {
		return true;
	}

	return purposes.every((v) => consents?.purposes[v]);
}
